/**
 * Centralized Email Templates
 * 
 * This file contains all email template functions used throughout the application.
 * All emails share the same header/footer design with REM branding.
 * Only the internal content varies based on email type.
 */

// REM Logo URL
export const REM_LOGO_URL = 'https://webfiles.remapp.ae/web_remapp_ae/hjas-1-1.png';

// Status colors mapping
export const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  in_progress: '#3b82f6',
  on_hold: '#6b7280',
  completed: '#10b981',
  cancelled: '#ef4444',
  confirmed: '#3b82f6',
  rescheduled: '#8b5cf6',
  no_show: '#6b7280',
  waiting_for_recording: '#f97316'
};

// Status gradients mapping
export const STATUS_GRADIENTS: Record<string, string> = {
  pending: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  in_progress: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  on_hold: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
  completed: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  cancelled: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  confirmed: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  rescheduled: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  no_show: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
  waiting_for_recording: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
};

// Status background colors mapping
export const STATUS_BG_COLORS: Record<string, string> = {
  pending: '#fef3c7',
  in_progress: '#dbeafe',
  on_hold: '#f3f4f6',
  completed: '#d1fae5',
  cancelled: '#fee2e2',
  confirmed: '#dbeafe',
  rescheduled: '#ede9fe',
  no_show: '#f3f4f6',
  waiting_for_recording: '#ffedd5'
};

// Status emojis mapping
export const STATUS_EMOJIS: Record<string, string> = {
  pending: '⏳',
  in_progress: '🔄',
  on_hold: '⏸️',
  completed: '✅',
  cancelled: '❌',
  confirmed: '📅',
  rescheduled: '🔄',
  no_show: '👻',
  waiting_for_recording: '🎥'
};

// Status labels mapping
export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  no_show: 'No Show',
  waiting_for_recording: 'Waiting for Recording'
};

/**
 * Base email wrapper with REM branding
 * All emails use this wrapper for consistent header/footer
 */
export const generateEmailWrapper = (
  headerTitle: string,
  headerSubtitle: string,
  emoji: string,
  gradientColors: string,
  accentColor: string,
  clientName: string,
  bodyContent: string,
  senderName: string
): string => {
  const currentYear = new Date().getFullYear();
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f5f7fa;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
    <!-- Logo Header -->
    <div style="background-color: #ffffff; padding: 25px 30px; text-align: center; border-bottom: 1px solid #e5e7eb;">
      <img src="${REM_LOGO_URL}" alt="REM - Real Estate Management" style="height: 50px; width: auto;" />
    </div>
    
    <!-- Header Banner -->
    <div style="background: ${gradientColors}; padding: 40px 30px; text-align: center;">
      <div style="font-size: 48px; margin-bottom: 10px;">${emoji}</div>
      <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 600;">${headerTitle}</h1>
      <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">${headerSubtitle}</p>
    </div>
    
    <!-- Content -->
    <div style="padding: 40px 30px;">
      <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px;">
        Dear <strong>${clientName}</strong>,
      </p>
      
      ${bodyContent}
      
      <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 30px 0 0;">
        Best regards,<br/>
        <strong style="color: ${accentColor};">${senderName}</strong>
      </p>
    </div>
    
    <!-- Footer -->
    <div style="background-color: #1f2937; padding: 30px; text-align: center;">
      <img src="${REM_LOGO_URL}" alt="REM" style="height: 30px; width: auto; margin-bottom: 15px; filter: brightness(0) invert(1);" />
      <p style="color: #9ca3af; font-size: 12px; margin: 0;">
        © ${currentYear} REM - Real Estate Management. All rights reserved.
      </p>
      <div style="margin-top: 15px;">
        <a href="https://remapp.ae" style="color: ${accentColor}; text-decoration: none; font-size: 12px;">www.remapp.ae</a>
      </div>
    </div>
  </div>
</body>
</html>`;
};

/**
 * Generate details table for tasks/meetings
 */
export const generateDetailsTable = (
  title: string,
  emoji: string,
  labelColor: string,
  rows: Array<{ label: string; value: string }>
): string => {
  const rowsHtml = rows.map((row, index) => `
    <tr>
      <td style="padding: 10px 0; color: ${labelColor}; width: 120px; ${index < rows.length - 1 ? 'border-bottom: 1px solid #e5e7eb;' : ''}"><strong>${row.label}:</strong></td>
      <td style="padding: 10px 0; color: #111827; ${index < rows.length - 1 ? 'border-bottom: 1px solid #e5e7eb;' : ''}">${row.value}</td>
    </tr>
  `).join('');

  return `
    <div style="background-color: #f8fafc; border-radius: 12px; padding: 25px; margin: 25px 0;">
      <h3 style="color: #1f2937; font-size: 16px; margin: 0 0 15px;">${emoji} ${title}</h3>
      <table style="width: 100%; border-collapse: collapse;">
        ${rowsHtml}
      </table>
    </div>
  `;
};

/**
 * Generate info box (colored left border)
 */
export const generateInfoBox = (
  bgColor: string,
  borderColor: string,
  textColor: string,
  content: string
): string => {
  return `
    <div style="background: ${bgColor}; border-left: 4px solid ${borderColor}; padding: 15px 20px; border-radius: 0 8px 8px 0; margin: 25px 0;">
      <p style="color: ${textColor}; font-size: 14px; margin: 0; line-height: 1.6;">
        ${content}
      </p>
    </div>
  `;
};

/**
 * Generate status badge
 */
export const generateStatusBadge = (status: string): string => {
  const bgColor = STATUS_BG_COLORS[status] || '#f3f4f6';
  const textColor = STATUS_COLORS[status] || '#374151';
  const label = STATUS_LABELS[status] || status.replace(/_/g, ' ');

  return `
    <div style="background-color: ${bgColor}; border-radius: 12px; padding: 20px; margin: 20px 0; text-align: center;">
      <p style="color: #374151; font-size: 14px; margin: 0 0 8px;">New Status:</p>
      <p style="color: ${textColor}; font-size: 20px; font-weight: 700; margin: 0; text-transform: uppercase; letter-spacing: 1px;">
        ${label}
      </p>
    </div>
  `;
};

/**
 * Generate CTA button
 */
export const generateButton = (
  text: string,
  href: string,
  bgColor: string
): string => {
  return `
    <div style="text-align: center; margin: 25px 0;">
      <a href="${href}" style="background-color: ${bgColor}; color: white; padding: 14px 35px; text-decoration: none; border-radius: 8px; display: inline-block; font-weight: 600; font-size: 14px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
        ${text}
      </a>
    </div>
  `;
};

// ========================================
// MEETING EMAIL TEMPLATES
// ========================================

export type MeetingEmailType = 'booking' | 'reminder' | 'status_change';

interface MeetingEmailData {
  meeting: {
    _id: string;
    subject: string;
    date: string;
    start_time: string;
    end_time: string;
    meetingType: 'online' | 'on-site';
    meeting_link?: string;
    assignedAgentId?: { name?: string; username?: string };
    assignedAgentIds?: Array<{ name?: string; username?: string }>;
  };
  clientName: string;
  companyName?: string;
  senderName: string;
  newStatus?: string;
  statusNotes?: string;
  recordingLink?: string;
  reviewLink?: string;
}

export const getMeetingEmailTemplate = (
  type: MeetingEmailType,
  data: MeetingEmailData
): { subject: string; body: string } => {
  const { meeting, clientName, companyName = '', senderName, newStatus, statusNotes, recordingLink, reviewLink } = data;

  const meetingDate = new Date(meeting.date).toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const meetingTime = `${meeting.start_time} - ${meeting.end_time}`;
  const meetingLink = meeting.meeting_link || '';
  // Meeting details table - matching the client detail page format
  const detailRows = [
    { label: 'Date', value: meetingDate },
    { label: 'Time', value: meetingTime },
    { label: 'Type', value: meeting.meetingType === 'online' ? 'Online Meeting' : 'On-site Meeting' },
    ...(meetingLink ? [{ label: 'Link', value: `<a href="${meetingLink}" style="color: #3b82f6; text-decoration: none;">${meetingLink}</a>` }] : [])
  ];

  const meetingDetailsTable = generateDetailsTable(
    'Meeting Details',
    '📅',
    STATUS_COLORS[newStatus || 'pending'] || '#3b82f6',
    detailRows
  );

  // Join meeting button
  const joinButton = meetingLink ? generateButton('🔗 Join Meeting', meetingLink, STATUS_COLORS[newStatus || 'confirmed'] || '#3b82f6') : '';

  const templates: Record<MeetingEmailType, { subject: string; body: string }> = {
    booking: {
      subject: `Meeting Confirmation: ${meeting.subject}`,
      body: generateEmailWrapper(
        'Meeting Confirmed!',
        meeting.subject,
        '✅',
        'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        '#3b82f6',
        clientName,
        `
          <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px;">
            We are pleased to confirm your meeting with <strong>${companyName}</strong>. Please find the details below.
          </p>
          
          ${meetingDetailsTable}
          ${joinButton}
          
          ${generateInfoBox(
          'linear-gradient(135deg, #3b82f615 0%, #1d4ed815 100%)',
          '#3b82f6',
          '#1e40af',
          '<strong>📝 Note:</strong> Please ensure you are available at the scheduled time. If you need to reschedule, please contact us as soon as possible.'
        )}
        `,
        senderName
      )
    },
    reminder: {
      subject: `Reminder: ${meeting.subject}`,
      body: generateEmailWrapper(
        'Meeting Reminder',
        'Don\'t forget your upcoming meeting!',
        '⏰',
        'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        '#d97706',
        clientName,
        `
          <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px;">
            This is a friendly reminder about your upcoming meeting. Please review the details below.
          </p>
          
          ${meetingDetailsTable}
          ${joinButton}
          
          ${generateInfoBox(
          'linear-gradient(135deg, #fef3c715 0%, #fcd34d15 100%)',
          '#f59e0b',
          '#92400e',
          '<strong>💡 Tip:</strong> Please be ready a few minutes before the scheduled time to ensure a smooth start.'
        )}
        `,
        senderName
      )
    },
    status_change: {
      subject: `Meeting Status Update: ${meeting.subject} - ${(newStatus || '').replace(/_/g, ' ').toUpperCase()}`,
      body: generateEmailWrapper(
        'Status Update',
        'Your meeting status has been changed',
        STATUS_EMOJIS[newStatus || 'pending'] || '📋',
        STATUS_GRADIENTS[newStatus || 'pending'] || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        STATUS_COLORS[newStatus || 'pending'] || '#667eea',
        clientName,
        `
          <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px;">
            The status of your meeting has been updated.
          </p>
          
          ${generateStatusBadge(newStatus || 'pending')}
          ${meetingDetailsTable}
          
          ${statusNotes ? `
          <div style="background: #f8fafc; border-radius: 12px; padding: 20px; margin: 25px 0;">
            <h4 style="color: #374151; font-size: 14px; font-weight: 600; margin: 0 0 10px; display: flex; align-items: center;">
              📝 Meeting Notes
            </h4>
            <p style="color: #4b5563; font-size: 14px; line-height: 1.6; margin: 0; white-space: pre-wrap;">
              ${statusNotes}
            </p>
          </div>
          ` : ''}
          
          ${newStatus === 'completed' && recordingLink ? `
          <div style="background: linear-gradient(135deg, #3b82f615 0%, #6366f115 100%); border-radius: 12px; padding: 20px; margin: 25px 0; border: 1px solid #3b82f630;">
            <h4 style="color: #1e40af; font-size: 14px; font-weight: 600; margin: 0 0 10px;">
              🎥 Meeting Recording Available
            </h4>
            <p style="color: #4b5563; font-size: 14px; line-height: 1.6; margin: 0 0 15px;">
              The recording of your meeting is now available. Please download it using the link below.
            </p>
            ${generateButton('📥 Download Recording', recordingLink, '#3b82f6')}
            ${generateInfoBox('#fef3c7', '#f59e0b', '#92400e', '<strong>⚠️ Important:</strong> Please download the recording within <strong>7 days</strong>. After this period, the recording will be automatically deleted from our servers.')}
          </div>
          ` : ''}
          
          ${newStatus === 'cancelled' ? generateInfoBox('#fee2e2', '#ef4444', '#991b1b', '<strong>⚠️ Important:</strong> If you did not request this cancellation or have any questions, please contact us immediately.') : ''}
          
          ${newStatus === 'completed' && !recordingLink ? generateInfoBox('#d1fae5', '#10b981', '#065f46', '<strong>🙏 Thank you</strong> for attending the meeting. We hope it was productive! Feel free to reach out if you have any follow-up questions.') : ''}
          
          ${newStatus === 'completed' && reviewLink ? `
          <div style="background: linear-gradient(135deg, #f59e0b15 0%, #fbbf2415 100%); border-radius: 12px; padding: 25px; margin: 25px 0; border: 1px solid #f59e0b30; text-align: center;">
            <div style="font-size: 40px; margin-bottom: 10px;">⭐</div>
            <h4 style="color: #92400e; font-size: 16px; font-weight: 600; margin: 0 0 10px;">
              How was your meeting?
            </h4>
            <p style="color: #78350f; font-size: 14px; line-height: 1.6; margin: 0 0 20px;">
              We'd love to hear your feedback! Please take a moment to rate your meeting experience.
            </p>
            ${generateButton('⭐ Rate Your Meeting', reviewLink, '#f59e0b')}
            <p style="color: #92400e; font-size: 12px; margin-top: 15px;">
              Your feedback helps us improve our services
            </p>
          </div>
          ` : ''}
          
          ${newStatus === 'confirmed' ? `
            ${generateInfoBox('#dbeafe', '#3b82f6', '#1e40af', '<strong>✅ Your meeting is confirmed!</strong> We look forward to meeting with you at the scheduled time.')}
            ${joinButton}
          ` : ''}
          
          ${newStatus === 'rescheduled' ? generateInfoBox('#fef3c7', '#f59e0b', '#92400e', '<strong>📅 Rescheduled:</strong> Please wait for an updated meeting invitation with the new schedule.') : ''}
          
          ${newStatus === 'no_show' ? generateInfoBox('#fef3c7', '#f59e0b', '#92400e', '<strong>📝 Note:</strong> You were marked as no-show for this meeting. If this was an error or you\'d like to reschedule, please contact us.') : ''}
        `,
        senderName
      )
    }
  };

  return templates[type];
};

// ========================================
// TASK EMAIL TEMPLATES
// ========================================

export type TaskEmailType = 'task_created' | 'status_update' | 'completed';

interface TaskEmailData {
  task: {
    _id: string;
    taskNumber?: string;
    title: string;
    description?: string;
    status: string;
    createdAt?: string;
    dueDate?: string;
  };
  clientName: string;
  senderName: string;
  comment?: string;
}

export const getTaskEmailTemplate = (
  type: TaskEmailType,
  data: TaskEmailData
): { subject: string; body: string } => {
  const { task, clientName, senderName, comment = '' } = data;

  const createdDate = task.createdAt ? new Date(task.createdAt).toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }) : 'N/A';

  const currentStatus = task.status || 'pending';
  const isNewTask = type === 'task_created';
  const isCompleted = type === 'completed' || currentStatus === 'completed';

  const subject = isNewTask
    ? `New Task Created: ${task.title}${task.taskNumber ? ` [${task.taskNumber}]` : ''}`
    : `Task Update: ${task.title} - ${STATUS_LABELS[currentStatus] || currentStatus}${task.taskNumber ? ` [${task.taskNumber}]` : ''}`;

  const headerTitle = isNewTask ? 'New Task Created' : isCompleted ? 'Task Completed!' : 'Task Status Update';
  const headerEmoji = isNewTask ? '📋' : (STATUS_EMOJIS[currentStatus] || '📋');
  const headerGradient = isNewTask ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : STATUS_GRADIENTS[currentStatus] || STATUS_GRADIENTS.pending;
  const accentColor = isNewTask ? '#3b82f6' : STATUS_COLORS[currentStatus] || STATUS_COLORS.pending;

  const introText = isNewTask
    ? 'A new task has been created regarding your account. We will keep you updated on its progress and notify you once it\'s completed.'
    : isCompleted
      ? 'We are pleased to inform you that your task has been completed!'
      : 'The status of your task has been updated.';

  // Task details rows (Due Date removed as per user request)
  const detailRows = [
    { label: 'Task ID', value: task.taskNumber || 'N/A' },
    { label: 'Title', value: task.title },
    { label: 'Created', value: createdDate },
    { label: 'Status', value: `<strong style="color: ${accentColor};">${STATUS_LABELS[currentStatus] || currentStatus}</strong>` }
  ];

  const body = generateEmailWrapper(
    headerTitle,
    task.title,
    headerEmoji,
    headerGradient,
    accentColor,
    clientName,
    `
      <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px;">
        ${introText}
      </p>
      
      ${generateDetailsTable('Task Details', '📋', accentColor, detailRows)}
      
      ${task.description ? generateInfoBox('#f0f9ff', accentColor, '#374151', `<strong>📝 Description:</strong><br/>${task.description}`) : ''}
      
      ${comment ? generateInfoBox('#fef3c7', '#f59e0b', '#374151', `<strong>💬 Update from our team:</strong><br/>${comment}`) : ''}
      
      ${isCompleted ? generateInfoBox('#d1fae5', '#10b981', '#065f46', '<strong>🎉 Thank you for your patience!</strong> If you have any questions or need further assistance, please don\'t hesitate to reach out.') : ''}
      
      ${isNewTask ? generateInfoBox('#dbeafe', '#3b82f6', '#1e40af', '<strong>📌 We\'ll keep you updated!</strong> You will receive notifications as we make progress on this task.') : ''}
    `,
    senderName
  );

  return { subject, body };
};

// ========================================
// QUICK ACCESS EMAIL TEMPLATES
// (For header quick access menu)
// Uses the same templates as Client Detail page for consistency
// ========================================

interface QuickAccessEmailData {
  type: 'task' | 'meeting';
  item: {
    _id: string;
    // Task fields
    taskNumber?: string;
    title?: string;
    description?: string;
    createdAt?: string;
    dueDate?: string;
    // Meeting fields
    subject?: string;
    date?: string;
    start_time?: string;
    end_time?: string;
    meetingType?: 'online' | 'on-site';
    meeting_link?: string;
  };
  clientName: string;
  clientEmail?: string;
  status: string;
  comment?: string;
  senderName: string;
}

export const getQuickAccessStatusEmailTemplate = (data: QuickAccessEmailData): { subject: string; html: string } => {
  const { type, item, clientName, status, comment = '', senderName } = data;

  if (type === 'task') {
    // Use the exact same template as Client Detail page for tasks
    const template = getTaskEmailTemplate('status_update', {
      task: {
        _id: item._id,
        taskNumber: item.taskNumber,
        title: item.title || '',
        description: item.description,
        status: status,
        createdAt: item.createdAt,
        dueDate: item.dueDate
      },
      clientName,
      senderName,
      comment
    });
    return { subject: template.subject, html: template.body };
  } else {
    // Use the exact same template as Client Detail page for meetings
    const template = getMeetingEmailTemplate('status_change', {
      meeting: {
        _id: item._id,
        subject: item.subject || '',
        date: item.date || '',
        start_time: item.start_time || '',
        end_time: item.end_time || '',
        meetingType: item.meetingType || 'online',
        meeting_link: item.meeting_link
      },
      clientName,
      senderName,
      newStatus: status,
      statusNotes: comment
    });
    return { subject: template.subject, html: template.body };
  }
};

